import { queryOptions } from "@tanstack/react-query";
import sortBy from "lodash-es/sortBy";
import { z } from "zod";

import { hasMatch, isMatchingSearch } from "@/common/utils/search";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";

import { companyQuery } from "./get-company";

type Storage = z.infer<typeof storagesSchema>;
const storagesSchema = z.object({
  address: z.string(),
  id: z.string(),
  mark: z.boolean().default(false),
  name: z.string(),
  offers: z.array(
    z.object({
      amount: z.number(),
      crop: z.object({
        id: z.string(),
        mark: z.boolean().default(false),
        name: z.string(),
      }),
      harvestYear: z.number(),
      id: z.string(),
      lastPrice: z.number().or(z.undefined()),
      orderAmount: z.number(),
      totalAmount: z.number(),
    }),
  ),
});

const storagesQuery = ({
  companyId,
  currency,
  locale,
  search,
}: {
  companyId: string;
  currency: Currency;
  locale: string;
  search?: string;
}) =>
  queryOptions({
    ...companyQuery({ currency, id: companyId, locale }),
    queryKey: ["storages", currency],
    select: (data) => {
      const result: Storage[] | undefined = data.company?.storages?.edges
        ?.filter((_storage) => !_storage?.node?.archived)
        .map((_storage) => {
          const storage = {
            address: _storage?.node?.address,
            id: _storage?.node?.id,
            mark: hasMatch(_storage?.node?.label, search),
            name: _storage?.node?.label,
            offers: _storage?.node?.offers?.edges
              ?.filter((node) => node?.node?.yearHarvested)
              .map((node) => ({
                amount: node?.node?.amount,
                crop: {
                  ...node?.node?.crop,
                  mark: hasMatch(node?.node?.crop?.name, search),
                },
                harvestYear: node?.node?.yearHarvested,
                id: node?.node?.id,
                lastPrice: _storage.node?.lastPrices?.edges?.find(
                  (price) => price?.node?.crop?.id === node?.node?.crop?.id,
                )?.node?.price,
                orderAmount:
                  node?.node?.orders?.edges
                    ?.filter((order) => order?.node?.active)
                    .reduce(
                      (acc, order) => acc + (order?.node?.amount ?? 0),
                      0,
                    ) ?? 0,
                totalAmount: node?.node?.totalAmount,
              })),
          };

          const safeStorage = storagesSchema.parse(storage);

          safeStorage.offers = sortBy(safeStorage.offers, (a) => [
            a.crop.name,
            a.harvestYear,
          ]);
          if (isMatchingSearch(safeStorage, search)) {
            return safeStorage;
          }
        })
        .filter((storage): storage is Storage => !!storage)
        .toSorted((a, b) => a.name.localeCompare(b.name));
      return result;
    },
  });

export { storagesQuery };
export type { Storage };
